'use client'
import NavigateRightIcon from '@/app/icons/navigate-right'
import { AdDecision } from '@/app/util/ads-endpoint'
import { addProtocol } from '@/app/util/url'
import Attribution from '@/components/attribution-v2'

import { useIsMobile } from '@/app/util/use-media-query'
import { AttrOverlay } from '@/components/grid/overlay/gif-overlay'
import { cn } from '@/util/cn'
import { GifOverlayProps, Video, VolumeOffIcon, VolumeOnIcon } from '@giphy/react-components'
import { SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { spotlightWidth } from '../spotlight-grid/spotlight-util'

const firePixel = (label: string, rawUrl?: string | null) => {
    if (!rawUrl) return
    const url = rawUrl.replace(/^"+|"+$/g, '')
    try {
        void fetch(url, { method: 'GET', mode: 'no-cors', keepalive: true })
    } catch {
        const i = new Image()
        i.src = url
    }
}

function useAttentionPauseResume(opts: { onPause?: () => void; onResume?: () => void; trackViewport?: boolean }) {
    const { onPause, onResume, trackViewport } = opts
    const containerRef = useRef<HTMLDivElement | null>(null)
    const activeRef = useRef(true)

    const setActive = useCallback(
        (next: boolean) => {
            if (next === activeRef.current) return
            activeRef.current = next
            next ? onResume?.() : onPause?.()
        },
        [onPause, onResume]
    )

    useEffect(() => {
        const onVis = () => setActive(!document.hidden)
        const onFocus = () => setActive(true)
        const onBlur = () => setActive(false)
        const onPageHide = () => setActive(false)

        document.addEventListener('visibilitychange', onVis)
        window.addEventListener('focus', onFocus)
        window.addEventListener('blur', onBlur)
        window.addEventListener('pagehide', onPageHide)

        let io: IntersectionObserver | undefined
        if (trackViewport && containerRef.current) {
            io = new IntersectionObserver(
                (entries) => {
                    const e = entries[0]
                    setActive(e.isIntersecting && e.intersectionRatio > 0)
                },
                { threshold: [0] }
            )
            io.observe(containerRef.current)
        }

        return () => {
            document.removeEventListener('visibilitychange', onVis)
            window.removeEventListener('focus', onFocus)
            window.removeEventListener('blur', onBlur)
            window.removeEventListener('pagehide', onPageHide)
            io?.disconnect()
        }
    }, [setActive, trackViewport])

    return { containerRef }
}

const GridOverlay = ({
    gif,
    data,
    muted,
    toggleMuted,
}: GifOverlayProps & { data: AdDecision; muted: boolean; toggleMuted: () => void }) => {
    const onClick = (e: SyntheticEvent) => {
        e.preventDefault()
        e.stopPropagation()
        if (data.cta_url) {
            window.open(addProtocol(data.cta_url), '_blank')
        }
    }
    const isMobile = useIsMobile()
    return (
        <div className="absolute inset-0 isolate cursor-pointer">
            {data?.cta_url && <div className="absolute inset-0" onClick={onClick} />}
            {isMobile ? (
                <AttrOverlay gif={gif} isHovered={true} width={spotlightWidth} />
            ) : (
                <Attribution user={gif.user} className="absolute bottom-2 left-2 text-shadow-lg/10" />
            )}
            <div
                className="absolute right-3 top-3 cursor-pointer hover:scale-125 transition-transform"
                onClick={toggleMuted}
                role="button"
                aria-label={muted ? 'Unmute' : 'Mute'}
                aria-pressed={muted}
            >
                {muted ? <VolumeOffIcon size={32} /> : <VolumeOnIcon size={32} />}
            </div>
            {data?.cta_url && (
                <div
                    className={cn(
                        'text-giphyWhite/75 absolute bottom-[10px] -right-1 flex h-[32px] cursor-pointer items-center justify-center gap-2 rounded-md px-5 font-bold hover:underline',
                        !isMobile && 'bg-giphyBlack/50 h-[50px] right-2'
                    )}
                    onClick={onClick}
                >
                    {data.cta_text} <NavigateRightIcon className="fill-giphyWhite/75 size-3" />
                </div>
            )}
        </div>
    )
}

const VideoSpotlight = ({ data, className }: { data: AdDecision; className?: string }) => {
    const { gif, analytics, impression_url } = data
    const aspectRatio = gif.images.original.width / gif.images.original.height
    const [muted, setMuted] = useState(true)

    // Used for tracking when a user removes or restores focus to the video
    const { containerRef } = useAttentionPauseResume({
        onPause: () => {
            if (analytics?.pause) firePixel('pause', analytics.pause)
        },
        onResume: () => {
            if (analytics?.resume) firePixel('resume', analytics.resume)
        },
        trackViewport: false,
    })

    //Analytics Events
    const handleCanPlay = () => {
        if (analytics?.start) firePixel('start', analytics.start)
    }

    const handleFirstPlay = () => {
        if (impression_url) firePixel('impression', impression_url)
    }

    const [userMuted, setUserMuted] = useState<boolean>(false) // wait for user interaction to fire events
    useEffect(() => {
        if (!userMuted) {
            return
        }
        if (muted) {
            if (analytics?.mute) firePixel('mute', analytics.mute)
        } else {
            if (analytics?.unmute) firePixel('unmute', analytics.unmute)
        }
    }, [muted, analytics])

    const handleQuartile = (q: 0.25 | 0.5 | 0.75) => {
        if (q === 0.25 && analytics?.firstq) firePixel('firstq', analytics.firstq)
        if (q === 0.5 && analytics?.midpoint) firePixel('midpoint', analytics.midpoint)
        if (q === 0.75 && analytics?.thirdq) firePixel('thirdq', analytics.thirdq)
    }

    const handleLoop = () => {
        if (analytics?.complete) firePixel('complete', analytics.complete)
    }

    const Controls = useMemo(() => {
        const Overlay = (props: GifOverlayProps) => {
            return (
                <GridOverlay
                    {...props}
                    data={data}
                    muted={muted}
                    toggleMuted={() => {
                        setUserMuted(true)
                        setMuted((m) => !m)
                    }}
                />
            )
        }
        Overlay.displayName = 'GridOverlayControls'
        return Overlay
    }, [data, muted])
    return (
        <div ref={containerRef} className={cn('relative', className)}>
            <Video
                // eslint-disable-next-line better-tailwindcss/no-unregistered-classes
                className={cn('giphy-video-spotlight rounded-sm', className)}
                gif={gif}
                width={spotlightWidth}
                percentWidth="100%"
                muted={muted}
                controls
                hideAttribution
                hideMute
                hideTitle
                overlay={Controls}
                style={{ aspectRatio }}
                onCanPlay={handleCanPlay}
                onFirstPlay={handleFirstPlay}
                onQuartile={handleQuartile}
                onLoop={handleLoop}
            />
        </div>
    )
}
export default VideoSpotlight
